import { ReactElement, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, withRouter } from 'react-router-dom';
import { EIconSize } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy } from 'utils/generic/lazy';
import { redirectToUrl } from 'utils/generic/redirect-to-url';
import { getHeaderHeight } from 'utils/helpers/fixed-header';
import { appendCustomCode } from 'utils/other/customCode';
import { AToZData } from 'interfaces/generic';
import { AFTER_SIGN_IN_ACTION_TYPES, AfterSignIn } from 'services/afterSignIn';
import { BottomNavigationSkeleton } from './BottomNavigationSkeleton';
import { NavigationWrapper, TabName, TabWrapper } from './style';
import { AtoZMenuDefaultConfigs } from 'elements/AtoZMenu/constants';
import { GlobalIcon } from 'components/GlobalIcon';
import { addQuery, updateQuery } from 'components/QueryRoute';
import { useProfileClose } from 'hooks/auth/useProfileClose';
import { useBottomNavData } from 'hooks/useBottomNavData';
import { useSearchParams } from 'hooks/useSearchParams';
import { setHeaderState } from 'store/actions/app-data';
import { setMustOpen } from 'store/actions/betslip';
import { toggleActiveCategory } from 'store/actions/casino-data';
import { getMustOpen } from 'store/selectors/betslip';
import { getIsLoggedIn, getUser } from 'store/selectors/user-data';

const { AtoZMenuContent } = lazy(
  () =>
    import(
      /* webpackChunkName: "a-to-z-menu-content" */ 'components/AtoZMenuContent'
    )
);

const { NavigationItemBadge } = lazy(
  () =>
    import(
      /* webpackChunkName: "navigation-item-badge" */ './__partials/NavigationItemBadge'
    )
);

const _BottomNavigationContent = (): ReactElement => {
  const [loading, setLoading] = useState(true);
  const [betslipOpened, setBetslipOpened] = useState(false);
  const [openBetslipListener, setOpenBetslipListener] = useState(false);

  const bottomNavData = useBottomNavData(setLoading);
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [showOpenBets, setShowOpenBets] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const profileClose = useProfileClose();
  const mountedRef = useRef(true);
  const user = useSelector(getUser);
  const { t } = useTranslation();
  const queries = useSearchParams();
  const lang = SpringConfigs.CURRENT_LANGUAGE;
  const mustOpen = useSelector(getMustOpen);

  useEffect(() => {
    const height = getHeaderHeight();

    dispatch(setHeaderState({ height }));
  }, [mustOpen]);

  const afterSigninAction = () => {
    return new Promise(resolve => {
      if (!mountedRef.current) {
        resolve(true);

        return null;
      }

      profileClose().then(() => {
        if (!mountedRef.current) {
          resolve(true);

          return null;
        }

        dispatch(setMustOpen(true));
        resolve(true);
      });
    });
  };

  useEffect(() => {
    if (
      bottomNavData.find(item => item.type === 'betslip') &&
      SpringConfigs?.IS_BOOK_BET_AVAILABLE &&
      SpringConfigs?.IS_BOOK_BET_SHARING_AVAILABLE &&
      queries.booking_id
    ) {
      if (!isLoggedIn && !user.pending && !betslipOpened) {
        history.push(addQuery({ accounts: '*', login: '*' }));
        setBetslipOpened(true);
        const afterSignIn = new AfterSignIn();
        afterSignIn.appendAction(
          AFTER_SIGN_IN_ACTION_TYPES.BOOKING_BET_GET_EVENT_ID,
          afterSigninAction
        );
      } else {
        setBetslipOpened(true);
        dispatch(setMustOpen(true));
      }
    }

    return () => {
      mountedRef.current = false;
    };
  }, [bottomNavData.length, isLoggedIn, user.pending, betslipOpened]);

  useEffect(() => {
    if (SpringConfigs.IFRAME_SPORTSBOOK) {
      const betslipTabExist = bottomNavData.find(tab => tab.type === 'betslip');

      if (betslipTabExist && !openBetslipListener) {
        setOpenBetslipListener(true);
        window.addEventListener('message', message => {
          if (message?.data?.openBetslip) {
            dispatch(setMustOpen(true));
          }
        });
      }
    }
  }, [bottomNavData]);

  const styledRoot =
    process.env.REACT_APP_ENV === 'widgets'
      ? `body {
      padding-bottom: 70px;
    }`
      : `#root {
        padding-bottom: 70px;
      }`;

  return (
    <NavigationWrapper id="bottom-navigation">
      {!SpringConfigs.MOCKED_DATA && <style>{styledRoot}</style>}
      {visible && (
        <Suspense fallback={null}>
          <AtoZMenuContent
            open={open}
            setOpen={setOpen}
            setVisible={setVisible}
            configs={AtoZMenuDefaultConfigs}
          />
        </Suspense>
      )}
      {loading ? (
        <BottomNavigationSkeleton />
      ) : (
        bottomNavData.map(_tab => {
          // FIXME
          const tab = _tab as unknown as AToZData;

          if (tab.category === 'casino' && tab.badge) {
            return null;
          }

          let titleFallback = null;
          const isCustomPage = tab.id === 'customPage';

          if (isCustomPage) {
            if (typeof tab.title === 'string') {
              try {
                titleFallback = JSON.parse(tab.title);
              } catch (e) {
                console.warn(e);
              }
            }
          }

          const title =
            typeof tab.title !== 'string'
              ? tab.title[lang]
                ? tab.title[lang]
                : tab.title.fallback
              : titleFallback
              ? titleFallback?.[SpringConfigs.CURRENT_LANGUAGE_PREFIX] ||
                titleFallback.en
              : tab.title;

          let path =
            typeof tab.path !== 'string' && tab.path
              ? tab.path[lang]
                ? tab.path[lang]
                : tab.path.fallback
              : tab.path;

          let pathWithLang = path;

          let pathnames = [
            history.location.pathname.split('/')[1],
            history.location.pathname.split('/')[2]
          ];

          if (path && path.length > 1 && path?.[0] === '/') {
            path = path?.replace('/', '');
          }

          if (
            typeof SpringConfigs.IS_DEFAULT_LANGUAGE !== 'undefined' &&
            SpringConfigs.IS_DEFAULT_LANGUAGE !== 1
          ) {
            pathWithLang = `${SpringConfigs.CURRENT_LANGUAGE_PREFIX}/${path}`;

            pathnames = pathnames.map(
              pathname => `${SpringConfigs.CURRENT_LANGUAGE_PREFIX}/${pathname}`
            );
          }

          let iconPath;
          let iconUrl;

          if (tab.type === 'betslip' && SpringConfigs.BETSLIP_ICON) {
            iconUrl = `${SpringConfigs.USER_FILES_URL}/media/${SpringConfigs.BETSLIP_ICON}?v=${SpringConfigs.VERSION}`;
          } else if (tab.iconUrl) {
            iconUrl =
              tab.iconUrl.includes('https://') ||
              tab.iconUrl.includes('http://')
                ? tab.iconUrl
                : `${SpringConfigs.USER_FILES_URL}/media/${tab.iconUrl}?v=${SpringConfigs.VERSION}`;
          } else if (tab.icon) {
            iconPath = tab.icon.split('.');
          } else {
            switch (tab.category) {
              case 'sport':
                iconPath =
                  tab.type === 'live'
                    ? ['generic', 'colored', 'live']
                    : ['generic', 'colored', 'upcoming'];
                break;
              case 'casino':
                iconPath = ['casino', 'colored', 'placeholder'];
                break;
              default:
                iconPath = ['casino', 'colored', 'othergames'];
            }
          }

          const switchTabs = () => {
            if (isCustomPage) {
              Number(tab.customPageCodeEnable) &&
                typeof tab.customPageCode === 'string' &&
                tab.customPageCode?.length &&
                appendCustomCode(tab.customPageCode);
            } else {
              if (tab.type === 'betslip') {
                dispatch(setMustOpen(true));
              } else if (tab.type === 'favorite') {
                dispatch(toggleActiveCategory('favorite'));
                history.push(updateQuery({ category: 'favorite' }));
                window.scrollTo(0, 0);
              } else if (path) {
                redirectToUrl(path, tab as AToZData);
              }
            }
          };

          const activeTab =
            (pathWithLang &&
              (pathnames.includes(pathWithLang) ||
                history.location.pathname === pathWithLang) &&
              queries.category !== 'favorite') ||
            (queries.category === 'favorite' && tab.type === 'favorite');

          return (
            <TabWrapper
              data-testid={`${tab.category || ''}-${tab.type || ''}`}
              tabsCount={bottomNavData.length}
              key={tab.id}
              onClick={() => {
                switchTabs();
                tab.type === 'other' && setVisible(true);
              }}
            >
              <GlobalIcon
                lib={iconPath?.[0]}
                theme={iconPath?.[1]}
                name={iconPath?.[2]}
                url={iconUrl as string}
                size={EIconSize._20}
                style={{ position: 'relative', height: '20px' }}
              >
                {tab.type === 'betslip' ||
                tab.type === 'live' ||
                tab.type === 'favorite' ? (
                  <Suspense>
                    <NavigationItemBadge
                      tab={tab}
                      setShowOpenBets={setShowOpenBets}
                    />
                  </Suspense>
                ) : null}
              </GlobalIcon>
              <TabName
                className={activeTab ? 'active' : undefined}
                isActive={activeTab}
              >
                {tab.type === 'betslip' && showOpenBets
                  ? t('betslip.openBets')
                  : title === 'Betslip'
                  ? t('betslip.betslip')
                  : title}
              </TabName>
            </TabWrapper>
          );
        })
      )}
    </NavigationWrapper>
  );
};

export const BottomNavigationContent: ReturnType<typeof withRouter> =
  withRouter(_BottomNavigationContent);
